/**
 * Phase1Panel.tsx — Profile summary card (left column) for P9_Reveal.
 *
 * Shows the five dog profile rows (breed/age, weight, activity, food, health)
 * progressively as Phase 1 animates.
 *
 * In review phase, each row becomes a tappable button that opens the edit
 * confirmation modal (click-to-edit affordance).
 */

import React from 'react';
import { FASE_1_STAGES, RevealContext } from '../revealStages';
import { FASE_1_ICONS, STAGE_TO_STEP, STAGE_EDIT_LABEL } from './constants';

interface Phase1PanelProps {
  visibleFase1: number;
  isReview: boolean;
  revealCtx: RevealContext;
  nombre: string;
  /** i18n translator — same signature as AppConfigContext's `t`. */
  t: (key: string, fallback: string, params?: Record<string, string | number>) => string;
  onEditStage: (stageKey: string) => void;
}

export const Phase1Panel: React.FC<Phase1PanelProps> = ({
  visibleFase1,
  isReview,
  revealCtx,
  nombre,
  t,
  onEditStage,
}) => (
  <div className="w-full flex flex-col">
    {/* V26 (Santiago 2026-04-28): padding compacto en mobile (p-3
        vs p-5) y space-y reducido para que quepa más texto en las
        filas truncadas. Desktop mantiene su respiración original. */}
    <div className="w-full lg:h-full bg-white rounded-2xl shadow-sm border border-gray-100 p-3 sm:p-5 space-y-2 sm:space-y-3">
      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1 sm:mb-2">
        {t('p9.fase1_label', 'Perfil de {nombre}', { nombre })}
      </p>
      {FASE_1_STAGES.map((stage, idx) => {
        const icon = FASE_1_ICONS[stage.key] || '✓';
        const visible = idx < visibleFase1;
        const editTarget = STAGE_TO_STEP[stage.key];
        // Only enable the click-to-edit affordance once the row is
        // visible AND we know which step owns the data. During the
        // pre-reveal animation the row is also visually hidden, so
        // disabling early prevents stray click hits.
        const canEdit = visible && editTarget !== undefined && isReview;
        return (
          <button
            key={stage.key}
            type="button"
            onClick={canEdit ? () => onEditStage(stage.key) : undefined}
            disabled={!canEdit}
            aria-hidden={!visible}
            aria-label={canEdit ? `Editar ${STAGE_EDIT_LABEL[stage.key] || stage.key}` : undefined}
            className={`w-full flex items-center gap-3 text-left rounded-lg transition-all duration-300 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
            } ${canEdit ? 'hover:bg-gray-50 cursor-pointer -mx-1.5 px-1.5 py-1' : 'cursor-default'}`}
          >
            <span
              className={`text-base flex-shrink-0 transition-transform duration-300 ${
                visible ? 'scale-100' : 'scale-50'
              }`}
              style={{ transitionTimingFunction: 'cubic-bezier(0.34,1.56,0.64,1)' }}
            >
              {icon}
            </span>
            <span className="flex-1 min-w-0 text-sm text-gray-700 truncate">{stage.computeLabel(revealCtx)}</span>
            <span
              className={`text-sm font-bold flex-shrink-0 transition-all duration-200 ${
                visible ? 'opacity-100 scale-100' : 'opacity-0 scale-50'
              } ${canEdit ? 'text-[#008BBC]' : 'text-green-500'}`}
              aria-hidden="true"
            >
              {canEdit ? '✏️' : '✓'}
            </span>
          </button>
        );
      })}
    </div>
  </div>
);
